/**
 * InvoiceFilters Component - T111
 *
 * Combined filter panel for invoice list with all filter controls
 */
import { useState } from 'react';
import { GlassPanel } from '../ui';
import { StatusFilter } from './StatusFilter';
import { DateRangeFilter } from './DateRangeFilter';
import { InvoiceSearchBar } from './InvoiceSearchBar';
import type { InvoiceStatus } from '../../hooks/useInvoices';

export interface InvoiceFiltersValue {
  q: string;
  status: InvoiceStatus | 'all' | 'active';
  dateFrom: string | undefined;
  dateTo: string | undefined;
}

interface InvoiceFiltersProps {
  value: InvoiceFiltersValue;
  onChange: (value: InvoiceFiltersValue) => void;
  showDateFilter?: boolean;
}

export function InvoiceFilters({ value, onChange, showDateFilter = true }: InvoiceFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(
    value.dateFrom !== undefined || value.dateTo !== undefined
  );

  const handleSearchChange = (q: string) => {
    onChange({ ...value, q });
  };

  const handleStatusChange = (status: InvoiceStatus | 'all' | 'active') => {
    onChange({ ...value, status });
  };

  const handleDateChange = (dateFrom: string | undefined, dateTo: string | undefined) => {
    onChange({ ...value, dateFrom, dateTo });
  };

  const hasActiveFilters =
    value.q ||
    (value.status !== 'all' && value.status !== 'active') ||
    value.dateFrom ||
    value.dateTo;

  return (
    <GlassPanel className="mb-6">
      <div className="space-y-4">
        {/* Search and expand */}
        <div className="flex gap-2 lg:gap-4 items-start flex-col lg:flex-row">
          <div className="flex-1 w-full lg:min-w-[200px]">
            <InvoiceSearchBar value={value.q} onChange={handleSearchChange} />
          </div>

          {showDateFilter && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors shrink-0 ${
                isExpanded || value.dateFrom || value.dateTo
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Zeitraum
              {(value.dateFrom || value.dateTo) && (
                <span className="w-2 h-2 rounded-full bg-blue-400" />
              )}
            </button>
          )}
        </div>

        {/* Status filter */}
        <StatusFilter value={value.status} onChange={handleStatusChange} />

        {/* Date range filter (expandable) */}
        {showDateFilter && isExpanded && (
          <div className="pt-2 border-t border-white/5">
            <DateRangeFilter
              dateFrom={value.dateFrom}
              dateTo={value.dateTo}
              onChange={handleDateChange}
            />
          </div>
        )}

        {/* Active filters summary */}
        {hasActiveFilters && (
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2 pt-2 border-t border-white/5">
            <span className="text-xs text-white/40 break-words">
              {[
                value.q && `Suche: "${value.q}"`,
                value.status !== 'all' && value.status !== 'active' && `Status: ${value.status}`,
                (value.dateFrom || value.dateTo) &&
                  `Zeitraum: ${value.dateFrom || '...'} – ${value.dateTo || '...'}`,
              ]
                .filter(Boolean)
                .join(' • ')}
            </span>
            <button
              onClick={() =>
                onChange({ q: '', status: 'active', dateFrom: undefined, dateTo: undefined })
              }
              className="text-xs text-white/40 hover:text-white transition-colors shrink-0"
            >
              Alle Filter zurücksetzen
            </button>
          </div>
        )}
      </div>
    </GlassPanel>
  );
}
